import { useEffect } from "react";
import { siteConfig } from "@/site.config";

export type SEOProps = {
  title?: string;
  description?: string;
  canonicalPath?: string;
  ogImagePath?: string;
  noindex?: boolean;
  jsonLd?: Array<Record<string, unknown>>;
};

const META_RE = /meta\[(name|property)="([^"]+)"\]/;

const setMeta = (selector: string, content: string) => {
  let el = document.head.querySelector<HTMLMetaElement>(selector);
  if (!el) {
    const m = selector.match(META_RE);
    if (!m) return;
    el = document.createElement("meta");
    el.setAttribute(m[1], m[2]);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
};

const setLink = (rel: string, href: string) => {
  let el = document.head.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
};

const setJsonLd = (id: string, data: Record<string, unknown>) => {
  let el = document.head.querySelector<HTMLScriptElement>(
    `script[data-ld="${id}"]`
  );
  if (!el) {
    el = document.createElement("script");
    el.setAttribute("type", "application/ld+json");
    el.setAttribute("data-ld", id);
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(data);
};

const clearJsonLd = (id: string) => {
  document.head.querySelector(`script[data-ld="${id}"]`)?.remove();
};

export function SEO({
  title,
  description,
  canonicalPath,
  ogImagePath,
  noindex,
  jsonLd,
}: SEOProps) {
  useEffect(() => {
    const fullTitle = title
      ? siteConfig.seo.titleTemplate.replace("%s", title)
      : siteConfig.seo.defaultTitle;
    const fullDescription = description ?? siteConfig.seo.defaultDescription;
    const path = canonicalPath ?? window.location.pathname;
    const canonicalUrl = `${siteConfig.seo.canonicalBase}${path}`;
    const ogImage = `${siteConfig.seo.canonicalBase}${ogImagePath ?? siteConfig.seo.ogImage}`;

    document.title = fullTitle;
    setMeta('meta[name="description"]', fullDescription);
    setMeta('meta[property="og:title"]', fullTitle);
    setMeta('meta[property="og:description"]', fullDescription);
    setMeta('meta[property="og:url"]', canonicalUrl);
    setMeta('meta[property="og:image"]', ogImage);
    setMeta('meta[property="og:type"]', "website");
    setMeta('meta[property="og:locale"]', "pt_BR");
    setMeta('meta[property="og:site_name"]', siteConfig.brand.name);
    setMeta('meta[name="twitter:card"]', "summary_large_image");
    setMeta('meta[name="twitter:title"]', fullTitle);
    setMeta('meta[name="twitter:description"]', fullDescription);
    setMeta('meta[name="twitter:image"]', ogImage);
    setMeta(
      'meta[name="twitter:image:alt"]',
      `Visual ${siteConfig.brand.name}`
    );
    setMeta('meta[property="og:image:alt"]', `Visual ${siteConfig.brand.name}`);
    setMeta('meta[property="og:image:width"]', "1200");
    setMeta('meta[property="og:image:height"]', "630");
    setMeta('meta[property="og:image:type"]', "image/png");
    setMeta(
      'meta[name="robots"]',
      noindex
        ? "noindex, nofollow"
        : "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1"
    );
    setMeta(
      'meta[name="googlebot"]',
      noindex ? "noindex, nofollow" : "index, follow"
    );
    setMeta('meta[name="format-detection"]', "telephone=no");
    setMeta('meta[name="referrer"]', "strict-origin-when-cross-origin");
    if (siteConfig.brand.address.state) {
      setMeta('meta[name="geo.region"]', `BR-${siteConfig.brand.address.state}`);
    }
    if (siteConfig.brand.address.city) {
      setMeta('meta[name="geo.placename"]', siteConfig.brand.address.city);
    }
    setLink("canonical", canonicalUrl);

    // JSON-LD por rota: limpa anteriores marcados como "route" e injeta os novos
    document.head
      .querySelectorAll<HTMLScriptElement>('script[data-ld^="route-"]')
      .forEach(el => el.remove());
    (jsonLd ?? []).forEach((data, idx) => setJsonLd(`route-${idx}`, data));

    return () => {
      // Limpa JSON-LD route-specific ao desmontar (mas mantém Organization/LocalBusiness globais)
      document.head
        .querySelectorAll<HTMLScriptElement>('script[data-ld^="route-"]')
        .forEach(el => el.remove());
    };
  }, [title, description, canonicalPath, ogImagePath, noindex, jsonLd]);

  return null;
}

/** Injeta JSON-LD global (Organization + LocalBusiness). chamar uma vez no SiteShell. */
export function GlobalJsonLd({
  entries,
}: {
  entries: Array<{ id: string; data: Record<string, unknown> }>;
}) {
  useEffect(() => {
    entries.forEach(e => setJsonLd(e.id, e.data));
    return () => entries.forEach(e => clearJsonLd(e.id));
  }, [entries]);
  return null;
}
